import { useCallback, useEffect } from "react";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { ProductCategory, ProductTypeCommon } from "@utils/constants";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";

/**
 * Module-level set tracking which productTypes have shown the toast in
 * the current screen focus window. Prevents double-firing when FilterScreen
 * and SearchResultScreen are both mounted (web split view).
 * Cleared on unmount so the next navigation visit can show again.
 */
const shownToastSet = new Set<string>();

const parseSavedSearchPayload = (savedSearch: unknown): any | undefined => {
  if (savedSearch == null || savedSearch === "") return undefined;

  try {
    let params: any = savedSearch;
    if (typeof savedSearch === "string") {
      params = JSON.parse(decodeURI(savedSearch).replace(/"|"/g, '"'));
    }
    if (params?.search_options) {
      params = params.search_options;
    }
    return params && typeof params === "object" ? params : undefined;
  } catch {
    return undefined;
  }
};

/**
 * When a deeplink/saved search is present, that payload is the source of truth
 * for whether the current search is lab-grown jewelry. Falling back to the
 * active tab would reuse a stale "Lab-Grown Jewelry" section from a previous
 * visit (React Navigation merges params; Redux sectionName also lingers).
 *
 * Returns `undefined` when there is no parseable saved search so callers can
 * fall back to the current tab/section.
 */
const hasLabGrownSubType = (value: unknown) =>
  !!value &&
  typeof value === "object" &&
  (value as { sub_type?: string }).sub_type ===
    ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE;

const hasOwnKeys = (value: unknown) =>
  !!value && typeof value === "object" && Object.keys(value).length > 0;

export const isLabGrownJewelrySearch = (
  savedSearch?: unknown
): boolean | undefined => {
  const params = parseSavedSearchPayload(savedSearch);
  if (!params) return undefined;

  const labGrownSection = params[SearchFilterTypeEnum.LAB_GROWN_JEWELRY];
  const jewelrySection = params[SearchFilterTypeEnum.JEWELRY];

  if (
    hasLabGrownSubType(params) ||
    hasLabGrownSubType(labGrownSection) ||
    hasLabGrownSubType(jewelrySection)
  ) {
    return true;
  }

  // Nested section objects from saved searches; ignore empty `{}` placeholders.
  if (hasOwnKeys(labGrownSection) && !hasOwnKeys(jewelrySection)) {
    return true;
  }

  return false;
};

interface UseLabGrownToastParams {
  productType: ProductCategory;
  /** Active tab/section key, e.g. routes[index]?.key or sectionName */
  activeTabKey?: string;
  /** Incoming saved/new search payload; takes precedence over activeTabKey */
  savedSearch?: unknown;
  getAppSettings: any;
  userInfo: any;
  t: (key: string) => string;
}

export const useLabGrownToast = ({
  productType,
  activeTabKey,
  savedSearch,
  getAppSettings,
  userInfo,
  t,
}: UseLabGrownToastParams) => {
  useEffect(() => {
    return () => {
      shownToastSet.delete(productType);
    };
  }, [productType]);

  /**
   * Shows the appropriate lab-grown toast once per focus window.
   * Optional overrides let callers pass navigation-state params that are
   * already updated even if this component has not re-rendered yet.
   * Returns `true` if the LAB_GROWN_DIAMOND popup should also be opened.
   */
  const triggerLabGrownToast = useCallback(
    (overrides?: { savedSearch?: unknown; activeTabKey?: string }): boolean => {
      if (shownToastSet.has(productType)) return false;

      if (
        productType === ProductCategory.LAB_GROWN_DIAMOND &&
        userInfo.showLabGrownPopup()
      ) {
        showToast("", userInfo.getLabGrownText(), ToastTypes.INFO, true, true);
        shownToastSet.add(productType);
        return true;
      }

      if (
        productType === ProductCategory.JEWELRY &&
        getAppSettings?.displayLabGrownJewelryToast
      ) {
        const resolvedSavedSearch = overrides?.savedSearch ?? savedSearch;
        const resolvedTabKey = overrides?.activeTabKey ?? activeTabKey;
        const fromSavedSearch = isLabGrownJewelrySearch(resolvedSavedSearch);
        const isLabGrownJewelry =
          fromSavedSearch ??
          resolvedTabKey === SearchFilterTypeEnum.LAB_GROWN_JEWELRY;

        if (isLabGrownJewelry) {
          showToast(
            "",
            t("global.toast.labgrownjewelry.title"),
            ToastTypes.LABGROWN_JEWELRY,
            true,
            true
          );
          shownToastSet.add(productType);
        }
      }

      return false;
    },
    [productType, activeTabKey, savedSearch, getAppSettings, userInfo, t]
  );

  /** Clears the dedup key on blur so the next focus event can show again. */
  const clearLabGrownToast = useCallback(() => {
    shownToastSet.delete(productType);
  }, [productType]);

  return { triggerLabGrownToast, clearLabGrownToast };
};
